/*
 * ONBOARDING: the path choice, the fencer search, the push offer, then the
 * email summaries offer (direction §12, §2; decisions 498, 623).
 *
 * "Path choice asks about the relationship ('Who are you keeping track of?') with
 * three situation cards — 'I fence' / 'My kid fences' ('one or more of your
 * fencers') / 'I coach' — subtitle defusing commitment ('just sets up your
 * starting view; change anything later'), honest because the stored hint is
 * cosmetic. Fencer/Parent paths flow into the same search component as
 * manage-fencers."
 *
 * THE SUBTITLE IS LOAD-BEARING, not reassurance copy. §2: the path choice is "a
 * default-behavior hint (digest visibility, naming defaults), never a permission."
 * So "change anything later" is TRUE, and the screen is allowed to say it. If the
 * hint ever gated a capability, that sentence would become a lie and this screen
 * would need rewriting — which is the real reason the hint must stay cosmetic.
 *
 * WHY THE SEARCH IS ONE COMPONENT SHARED WITH MANAGE-FENCERS. §12 says so, and the
 * reason it matters is the cap: the arithmetic ("N of 10"), the at-cap blocked
 * commit, and the club-expansion behaviour are all the same rules in both places.
 * Two implementations would drift, and the one that drifted would be the
 * onboarding copy nobody looks at again after their first week.
 *
 * THE PUSH OFFER IS THE THIRD STEP AND IT SITS BEFORE THE COMMIT (decision 498),
 * which is the structural half of it. `Routes.tsx` decides somebody is onboarding
 * by `!user.current_list_id`, so the instant `completeOnboarding` returns that
 * gate closes and the app renders. A step AFTER the commit would therefore need
 * something else to make it appear exactly once — a column (a migration for a
 * screen) or a flag in memory (lost by a reload, on iOS, at the exact moment a
 * reader is installing the app). Before the commit, "exactly once" is the gate
 * that already exists: onboarding runs while there is no list, and that is once.
 *
 * The COMMIT IS NOT SPLIT to make room for it (decision 312). It is still one
 * call to one endpoint in one transaction; what changed is only which button
 * makes it. The price is real and is worth naming: pressing "Track 3 fencers" no
 * longer lands in the app. That is what the third step costs, and the step
 * reading as the last one is what pays for it.
 *
 * THE SUMMARIES OFFER IS A FOURTH STEP ON THE SAME RULE (decision 623): before
 * the commit, so "exactly once" is still the gate that exists, and its choice
 * rides in the same one call. It follows the push step, or the fencer step
 * where push is not offered, and is skipped with it on an empty list.
 */

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { FencerSearch } from "../components/FencerSearch";
import { SummaryChoice, emptySummaryDraft, needsCategory, type SummaryDraft } from "../components/SummaryChoice";
import { api, ApiError, type EmailSummary, type FencerResult, type User } from "../lib/api";
import { offerable, usePushEnrolment, type PushEnrolment } from "../lib/usePushEnrolment";
import styles from "./Onboarding.module.css";

type Persona = "fencer" | "parent" | "coach";

export interface OnboardingProps {
  /** The account onboarding: the summaries step names its address. */
  user: User;
  onDone: (user: User) => void;

  /**
   * The VAPID application server key from /api/me (stage 4a).
   *
   * Passed in rather than fetched, for the reason the notifications page gives:
   * `pushManager.subscribe` has to run inside the gesture handler, and a network
   * round trip between the tap and the prompt loses the gesture.
   *
   * Empty or absent is one of the four reasons the offer is not made at all.
   */
  pushPublicKey?: string;

  /**
   * The member cap from /api/me (decision 889), for the search's "N of M".
   * Onboarding has no list yet to carry one. Absent when the server could not
   * read it, and the search then states no limit and the server enforces.
   */
  cap?: number;
}

export function Onboarding({ user, onDone, pushPublicKey, cap }: OnboardingProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // FOUR steps in ONE screen component, deliberately not four routes. None of
  // them is a destination anybody bookmarks or comes back to, and giving one a
  // URL would put a step in the history that the back button walks into after
  // onboarding has already completed.
  const [persona, setPersona] = useState<Persona | null>(null);
  const [chosen, setChosen] = useState<FencerResult[]>([]);
  const [step, setStep] = useState<"fencers" | "push" | "summary">("fencers");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * MOUNTED HERE, at the top of the flow, rather than on the step that offers
   * the button — which buys the two things the offer needs to be ready:
   * `prepareRegistration()` has a whole path choice and a fencer search in which
   * to activate the worker, and the subscription probe has resolved long before
   * anything asks whether the step is worth showing.
   *
   * It prompts nothing. See lib/usePushEnrolment.ts.
   */
  const enrolment = usePushEnrolment(pushPublicKey ?? "", { sync: false });

  /**
   * The ONE write (decision 312). The summaries step's choice rides in it when
   * there is one, so the list and the summary settings land in one transaction
   * or not at all; "Not now" and a skipped step send none.
   */
  async function commit(summary?: EmailSummary) {
    setBusy(true);
    setError(null);
    try {
      const body = await api.completeOnboarding({
        ...(persona ? { persona_hint: persona } : {}),
        fencer_ids: chosen.map((f) => f.fencer_id),
        ...(summary ? { email_summary: summary } : {}),
      });
      onDone(body.user);
      navigate("/", { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError && err.failure.kind === "server" && err.failure.status === 409
          ? t("onboarding.errorCap")
          : t("error.couldNotSave"),
      );
    } finally {
      setBusy(false);
    }
  }

  /*
   * WHETHER THE OFFER IS WORTH MAKING, decided ONCE at the press rather than on
   * every render. After a refusal the browser reports `denied` and `offerable`
   * goes false, and a step that re-read it would un-render itself out from under
   * the reader it had just asked a question of.
   *
   * NOTHING CHOSEN IS THE FIFTH REASON TO SKIP, beside the four in
   * `offerable`. "Skip for now" is the supported look-around path, and the
   * offer's whole premise is that somebody has just said who they care about —
   * its sentence says "your fencer". Offering it to an empty list would be
   * promising news about nobody.
   */
  /*
   * THE EMPTY LIST SKIPS BOTH OFFERS, and for the same reason (decision 623
   * applying 498's fifth rule to the fourth step): a summary of nobody's news
   * would be a promise about nobody. Somebody who chose fencers goes to the push
   * offer when the browser can take one, and to the summaries step either way.
   */
  function proceed() {
    if (chosen.length === 0) void commit();
    else setStep(offerable(enrolment) ? "push" : "summary");
  }

  if (persona === null) {
    return <PathChoice onChoose={setPersona} />;
  }

  async function enableThenContinue() {
    // `enrolment.enable()` is reached synchronously from the click handler that
    // calls this, which is what keeps the gesture Safari requires.
    const outcome = await enrolment.enable();
    // `denied`, `dismissed` and `failed` stay on the step and say so:
    // navigating away the instant somebody answered "Block", or closed the
    // prompt, would read as though the button had worked. Everything else goes
    // on — including the `unsupported` that cannot happen here, because
    // stranding a reader on a screen whose control has no outcome is the worse
    // failure.
    if (outcome === "enabled" || outcome === "unsupported") setStep("summary");
  }

  if (step === "push") {
    return (
      <PushOffer
        enrolment={enrolment}
        busy={busy}
        error={error}
        onEnable={() => void enableThenContinue()}
        onSkip={() => setStep("summary")}
      />
    );
  }

  if (step === "summary") {
    return (
      <SummaryOffer
        user={user}
        busy={busy}
        error={error}
        onStart={(s) => void commit(s)}
        onSkip={() => void commit()}
      />
    );
  }

  return (
    <div className={styles.screen}>
      <h1 className={styles.heading}>
        {persona === "coach" ? t("onboarding.buildList") : t("onboarding.findFencer")}
      </h1>
      <p className={styles.sub}>
        {persona === "coach" ? t("onboarding.buildListSub") : t("onboarding.findFencerSub")}
      </p>

      <FencerSearch chosen={chosen} onChange={setChosen} {...(cap !== undefined ? { cap } : {})} />

      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}

      <div className={styles.commitBar}>
        {/*
          FILLED RED = the primary action, and it stays enabled with nothing
          chosen. A coach who wants to look around first gets an empty list rather
          than a blocked flow — Active and Upcoming then honestly show nothing,
          which is a state the app renders properly.
        */}
        <button className={styles.primary} type="button" onClick={proceed} disabled={busy}>
          {chosen.length === 0
            ? t("onboarding.continueEmpty")
            : t("onboarding.continueWith", { count: chosen.length })}
        </button>
        {/*
          Back to the path choice as a text link, not a chevron: it is a rare
          action and text is unambiguous. It does NOT use history.back(), because
          the path choice has no URL — see the state comment above.
        */}
        <button className={styles.textButton} type="button" onClick={() => setPersona(null)}>
          {t("onboarding.back")}
        </button>
      </div>
    </div>
  );
}

/**
 * The three situation cards.
 *
 * Real <button>s in a <ul>: they are actions, not links, because choosing a path
 * does not navigate anywhere the browser knows about. That is the one place in the
 * app where a button rather than an anchor is right — and the distinction is
 * exactly principle 1's, which protects real NAVIGATION rather than banning
 * buttons.
 */
function PathChoice({ onChoose }: { onChoose: (p: Persona) => void }) {
  const { t } = useTranslation();

  const paths: { id: Persona; title: string; detail: string }[] = [
    { id: "fencer", title: t("onboarding.iFence"), detail: t("onboarding.iFenceDetail") },
    { id: "parent", title: t("onboarding.kidFences"), detail: t("onboarding.kidFencesDetail") },
    { id: "coach", title: t("onboarding.iCoach"), detail: t("onboarding.iCoachDetail") },
  ];

  return (
    <div className={styles.screen}>
      <h1 className={styles.heading}>{t("onboarding.pathHeading")}</h1>
      {/* The commitment-defusing subtitle, which §2's cosmetic hint makes honest. */}
      <p className={styles.sub}>{t("onboarding.pathSub")}</p>

      <ul className={styles.cards}>
        {paths.map((p) => (
          <li key={p.id}>
            <button className={styles.card} type="button" onClick={() => onChoose(p.id)}>
              <span className={styles.cardTitle}>{p.title}</span>
              <span className={styles.cardDetail}>{p.detail}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * How long each example notification is shown, and how long the cross-fade takes.
 *
 * Long enough to READ two lines without hurrying, which is the only requirement
 * this number has: the card is an illustration, and a reader who misses one has
 * lost nothing. Short enough that somebody deciding whether to press the button
 * sees more than one before they do.
 */
const previewHoldMs = 3_500;

/**
 * Whether motion should be suppressed, asked of BOTH channels.
 *
 * The app honours two: the OS setting, and the in-app toggle that `main.tsx`
 * writes to `data-reduce-motion` on the root (decision 386). `tokens.css`
 * collapses every transition-duration for both, which is enough for CSS — and
 * is NOT enough here, because a collapsed duration turns this carousel's
 * cross-fade into a jump-cut every few seconds while the timer keeps running.
 * Motion has to be asked about in JavaScript to be stopped in JavaScript.
 *
 * Read once on mount rather than subscribed to: this screen is seen once, for
 * under a minute, and a reader does not change the setting while looking at it
 * — the toggle is on a page they cannot reach from here.
 */
function prefersStill(): boolean {
  if (typeof window === "undefined" || typeof document === "undefined") return true;
  if (document.documentElement.dataset.reduceMotion === "true") return true;
  return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
}

/**
 * THE EXAMPLE NOTIFICATIONS — an illustration of the sentence above them, and
 * the only decorative motion in the app (Kevin's ruling).
 *
 * WHAT IT IS FOR. "Check-in, pool and strip assignments, round changes" is
 * accurate and abstract, and the reader deciding here has, by definition, never
 * received one of these. Showing the shape of what arrives is the part a
 * sentence cannot do.
 *
 * UNDER REDUCED MOTION IT SHOWS THE FIRST ONE AND STOPS (Kevin's ruling). Not a
 * stacked list of all five: the card is meant to read as ONE notification, and
 * five at once would read as an inbox — a different claim about how much this
 * sends. The timer is not started at all, rather than started and hidden, so
 * nothing is scheduled that nobody will see.
 *
 * IT IS `aria-hidden` (Kevin's ruling). It carries no information the
 * description above does not already state, and a region that rewrites itself
 * every few seconds is actively hostile to a screen reader. This does not run
 * into direction §7's never-through-one-channel rule, which is about STATE: the
 * card introduces none, it illustrates a sentence that remains the accessible
 * channel. The icon's alt is empty for the same reason.
 *
 * THE COPY IS NOT COMPOSED FROM THE REAL PUSH STRINGS, and that is a deviation
 * Kevin ruled on directly ("not identical to what is shown in-app, but that is
 * ok"). All five TITLES are the approved shapes with example values filled in —
 * `checkedIn`, `poolAssigned`, `deTablePosted`, `deStripVs` and
 * `placedTie` — but three of the BODIES are not: two carry a tournament where
 * the real string carries a time, and the placement line adds a segment. It is
 * an illustration of the KIND of thing that arrives, not a specimen of the
 * wording. Recorded rather than left to be discovered, because decision 488
 * forbids composing a push sentence out of fragments and a reader could
 * reasonably take this card for the vocabulary.
 */
function PushPreview() {
  const { t } = useTranslation();

  // Written out rather than built in a loop: `literals.test.ts` can only see a
  // key named by a static translation call, and a template literal would hide
  // all ten of these from it (decision 423).
  const items = [
    { title: t("onboarding.push.example1Title"), body: t("onboarding.push.example1Body") },
    { title: t("onboarding.push.example2Title"), body: t("onboarding.push.example2Body") },
    { title: t("onboarding.push.example3Title"), body: t("onboarding.push.example3Body") },
    { title: t("onboarding.push.example4Title"), body: t("onboarding.push.example4Body") },
    { title: t("onboarding.push.example5Title"), body: t("onboarding.push.example5Body") },
  ];

  const [still] = useState(prefersStill);
  const [shown, setShown] = useState(0);

  useEffect(() => {
    if (still) return;
    const timer = setInterval(
      () => setShown((i) => (i + 1) % items.length),
      previewHoldMs,
    );
    return () => clearInterval(timer);
    // `items` is rebuilt every render; its LENGTH is the only thing this needs
    // and it is fixed, so depending on the array would restart the timer on
    // every tick and the card would never advance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [still]);

  return (
    <div className={styles.preview} data-push-preview aria-hidden="true">
      {(still ? items.slice(0, 1) : items).map((item, i) => (
        <div
          key={item.title}
          /*
            ALL FIVE ARE IN THE DOM AND STACKED, with only the current one
            opaque. Rendering one at a time and swapping would reflow the page
            on every change, because the bodies wrap to different heights at a
            phone's width — the note and the buttons below would step up and
            down for as long as the reader looked at it.
          */
          className={`${styles.previewItem} ${i === shown ? styles.previewItemShown : ""}`}
        >
          {/*
            THE REAL NOTIFICATION ICON, `/icon-192.png`, which is what `sw.ts`
            passes to `showNotification` — so this is the icon that will
            actually appear rather than a stand-in. It carries its own dark
            tile, which is why one asset reads on both grounds and no
            light/dark pair is needed here.
          */}
          <img className={styles.previewIcon} src="/icon-192.png" alt="" />
          <span className={styles.previewText}>
            <span className={styles.previewTitle}>{item.title}</span>
            <span className={styles.previewBody}>{item.body}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * THE PUSH OFFER — the third step, and the only place in the app that offers
 * this to somebody who has not gone looking for it (decision 498).
 *
 * FOUR PARTS AND NOTHING ELSE: what will arrive, a button that enables it, an
 * obvious way past, and where to change the answer. There is no heading about
 * permissions, no browser instruction and NO iOS INSTALL TUTORIAL (Kevin's
 * ruling) — a browser that cannot subscribe never reaches this step at all,
 * which is what `offerable` decides.
 *
 * IT HAS ITS OWN DESCRIPTION RATHER THAN THE SETTINGS PAGE'S (Kevin's copy,
 * reversing what was built first). `push.enableDetail` was reused here on the
 * argument that a PROMISE with two copies stops matching the defaults the first
 * time one is edited — but that sentence was written for somebody auditing a
 * channel they already have, where a first run is an introduction. So
 * `onboarding.push.detail` names the same day-of kinds and ends "and more",
 * which is also why the drift argument does not carry across the difference: a
 * sentence that does not claim to be complete cannot fall out of step with the
 * defaults the way a closed list does.
 *
 * The settings sentence has since stopped closing its list too (519): it no
 * longer ends "Nothing else, and never by email", so what separates the two is
 * now the VOICE — an introduction against a description — rather than
 * completeness or decision 392's rule, which neither screen states and three
 * non-copy places enforce.
 *
 * THE TWO CONTROLS CARRY EQUAL WEIGHT (Kevin's ruling): same width, one per
 * line, with a deliberate gap. Accepting and declining are both ordinary answers
 * here, and a row made "Not now" the small thing beside the big one. They still
 * look different — filled accent against outlined neutral, which is build rule
 * 1's primary slot — and the neutral is an outlined control rather than the
 * `.textButton` "Back" uses, on decision 480: a text button with no
 * `min-block-size` is the ~17px target that ruling was written about.
 *
 * THE DENIED STATE STAYS ON THE STEP. The offer is replaced rather than
 * annotated: the button cannot work a second time (the browser will not ask
 * again), so leaving it would be a control with no outcome, which is what
 * decision 418 refuses. The Settings note below is what makes the answer look
 * reversible, because it is.
 *
 * A FAILED attempt keeps the button, because "try again" is the actual remedy
 * for a network or a worker that would not activate.
 */
function PushOffer({
  enrolment,
  busy,
  error,
  onEnable,
  onSkip,
}: {
  enrolment: PushEnrolment;
  busy: boolean;
  error: string | null;
  onEnable: () => void;
  onSkip: () => void;
}) {
  const { t } = useTranslation();

  const denied = enrolment.result === "denied";
  const working = busy || enrolment.busy;

  return (
    <div className={styles.screen}>
      {/*
        THE HEADING STAYS OVER THE REFUSAL (Kevin's ruling). It reads as a
        question the reader has now answered rather than as one still being
        asked, and what it buys is that the page keeps its anchor: the same
        screen, in a different state, rather than an unfamiliar one.
      */}
      <h1 className={styles.heading}>{t("onboarding.push.heading")}</h1>
      <p className={styles.sub}>
        {denied ? t("onboarding.push.denied") : t("onboarding.push.detail")}
      </p>

      {/*
        The example, SHOWN ONLY WHILE THERE IS SOMETHING TO ILLUSTRATE. After a
        refusal the sentence above no longer describes what will arrive, so a
        card demonstrating it would be advertising over an answer.
      */}
      {denied ? null : <PushPreview />}

      {/*
        The way back, named as a real path rather than as "in Settings". A reader
        deciding whether to trust a lock screen is owed the same precision about
        undoing it as about what arrives.

        TWO NOTES, BECAUSE THE REMEDY IS DIFFERENT (Kevin's copy). After a
        refusal, "you can change this in Settings" is not true on its own: the
        browser will not ask again, so our page cannot re-arm the permission and
        the first move is the browser's or the system's. The denied note says
        that in order, and only then names our page. Which of the two it names
        first depends on whether the app is installed: a tab's block lives in
        the browser's site settings, an installed app's in the device's.
      */}
      <p className={styles.note}>
        {denied
          ? enrolment.installed
            ? t("onboarding.push.deniedNoteInstalled")
            : t("onboarding.push.deniedNote")
          : t("onboarding.push.settingsNote")}
      </p>

      {/*
        A CLOSED PROMPT IS NOT A REFUSAL: the browser will ask again, so the
        offer, the preview and "Not now" all stay and one line says what to do.
      */}
      {enrolment.result === "dismissed" ? (
        <p className={styles.note}>{t("push.dismissed")}</p>
      ) : null}

      {enrolment.result === "failed" ? (
        <p className={styles.error} role="alert">
          {t("error.couldNotSave")}
        </p>
      ) : null}

      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}

      <div className={styles.actions}>
        {denied ? null : (
          <button
            className={styles.primary}
            type="button"
            // The gesture: `enablePush` is reached synchronously from here, with
            // the key already in hand. Nothing awaits a network call first.
            onClick={onEnable}
            disabled={working}
          >
            {t("push.enable")}
          </button>
        )}
        <button
          className={styles.secondary}
          type="button"
          onClick={onSkip}
          disabled={working}
        >
          {denied ? t("onboarding.push.continue") : t("onboarding.push.notNow")}
        </button>
      </div>
    </div>
  );
}

/**
 * THE EMAIL SUMMARIES OFFER — the fourth step, before the commit (decision
 * 623, amending 498's count). Accounts that finished onboarding before it
 * existed find the same controls in Settings.
 *
 * NOTHING IS PRESELECTED (Kevin): no cadence and no category, so whatever the
 * reader ends up with is something they chose. "Start summaries" stays disabled
 * until there is a cadence and at least one box, which says what is missing
 * without an error; the push step's equal-weight pair is reused as it is, the
 * filled accent over the outlined neutral, one per line (498).
 *
 * THE CHOICE IS HELD HERE AND SENT WITH THE COMMIT, never saved on its own:
 * 312's one transaction, and "Not now" leaves nothing behind.
 *
 * AN UNVERIFIED ADDRESS IS SAID PLAINLY, not blocked. Every account arrives
 * here unverified unless it opened its link already, and summaries simply
 * wait for that (decision 620) -- so the step still records the choice and
 * tells the reader what starts it.
 */
function SummaryOffer({
  user,
  busy,
  error,
  onStart,
  onSkip,
}: {
  user: User;
  busy: boolean;
  error: string | null;
  onStart: (s: EmailSummary) => void;
  onSkip: () => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<SummaryDraft>(emptySummaryDraft);
  const ready = draft.frequency !== null && !needsCategory(draft);

  return (
    <div className={styles.screen}>
      <h1 className={styles.heading}>{t("onboarding.summary.heading")}</h1>
      {/* Settings' own sentence, deliberately the same key (Kevin's review):
          the two screens describe one thing, and one string cannot drift. */}
      <p className={styles.sub}>{t("emailSummary.note")}</p>

      <SummaryChoice value={draft} onChange={setDraft} withOff={false} idPrefix="onboarding-summary" disabled={busy} />

      {user.email_status === "unverified" ? (
        <p className={styles.note}>{t("onboarding.summary.unverifiedNote")}</p>
      ) : null}
      <p className={styles.note}>{t("onboarding.summary.settingsNote")}</p>

      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}

      <div className={styles.actions}>
        <button
          className={styles.primary}
          type="button"
          disabled={!ready || busy}
          onClick={() => draft.frequency && onStart({ ...draft, frequency: draft.frequency })}
        >
          {t("onboarding.summary.start")}
        </button>
        <button className={styles.secondary} type="button" onClick={onSkip} disabled={busy}>
          {t("onboarding.summary.notNow")}
        </button>
      </div>
    </div>
  );
}
